import React, { useEffect, useState, useRef } from "react";
import { useFormContext, useWatch, useFormState } from "react-hook-form";
import DateInput from "@shared/components/DateInput";
import {
  GoalDropdownValue,
  ProjectDropdownValue,
} from "@shared/interfaces/ProgramFiles";
import MultiSelectDropdown from "@pages/Program/components/MultiSelectDropdown";
import { useUserService } from "@utils/useUsers";
import { User } from "@shared/interfaces/User";
import { useGoal } from "@pages/Program/components/goal/utils/useGoal";
import { useParams, useSearchParams } from "react-router-dom";

const MilestoneForm: React.FC<{
  dataLoading?: boolean;
  startDate: string;
  endDate: string;
  onDelete?: () => void;
}> = ({ dataLoading, startDate, endDate, onDelete }) => {
  const {
    register,
    setValue,
    control,
    trigger,
    setError,
    clearErrors,
    watch,
    reset,
  } = useFormContext();

  const [projectTouched, setProjectTouched] = useState(false);
  const [goalTouched, setGoalTouched] = useState(false);

  // const project = useWatch({ name: "project_id", control });
  const values = watch();
  const { errors } = useFormState({ control });
  const [allProjects, setAllProjects] = useState<ProjectDropdownValue[]>([]);
  const [selectedProject, setSelectedProject] = useState<
    ProjectDropdownValue[]
  >([]);

  const [allGoals, setAllGoals] = useState<GoalDropdownValue[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<GoalDropdownValue[]>([]);

  const { searchCompanyMembers } = useUserService();
  const { fetchProgramProjectsDropdown } = useGoal();
  const [selectedResponsible, setSelectedResponsible] = useState<User[]>([]);
  const [allCompanyUsers, setAllCompanyUsers] = useState<User[]>([]);
  const { programId } = useParams();

  const { fetchGoalsDropdown } = useGoal();

  const formRef = useRef<HTMLFormElement | null>(null);

  const fetchUsers = async (
    query: string
  ): Promise<(User & { full_name: string })[]> => {
    const results = await searchCompanyMembers(query, true);

    // Scroll to the bottom of the form after fetching
    setTimeout(() => {
      formRef.current?.scrollTo({
        top: formRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 250);

    return (results || []).map((user: User) => ({
      ...user,
      full_name: `${user.first_name} ${user.last_name}`,
    }));
  };

  useEffect(() => {
    const fetchCompanyUsers = async () => {
      let results = await searchCompanyMembers("", true);
      results = (results || []).map((user: User) => ({
        ...user,
        full_name: `${user.first_name} ${user.last_name}`,
      }));
      setAllCompanyUsers(results);
    };
    fetchCompanyUsers();
  }, []);

  useEffect(() => {
    register("project_id", { required: "Project is required" });
    register("responsible_user_id");
  }, [register]);

  useEffect(() => {
    const fetchProjectDropdown = async () => {
      if (programId) {
        const data = await fetchProgramProjectsDropdown(programId);
        setAllProjects(data);
      }
    };

    const fetchGDropdown = async () => {
      if (programId) {
        const data = await fetchGoalsDropdown(programId);
        setAllGoals(data);
      }
    };

    fetchGDropdown();
    fetchProjectDropdown();
  }, []);

  const project = watch("project_id") || [];
  const responsibleUserId = watch("responsible_user_id") || [];
  const goal = watch("goal_id") || [];

  useEffect(() => {
    if (!allProjects.length) return;
    if (!allCompanyUsers.length) return;
    if (!allGoals.length) return;

    const matchedProject = allProjects.find((p) => p.id === project);
    const matchedGoal = allGoals.find((p) => p.id === goal);
    const responsibleUser = allCompanyUsers.find(
      (user) => user.id === responsibleUserId
    );

    setSelectedProject((prev) => {
      const prevId = prev?.[0]?.id ?? null;
      return matchedProject && matchedProject.id !== prevId
        ? [matchedProject]
        : prev;
    });

    setSelectedGoal((prev) => {
      const prevId = prev?.[0]?.id ?? null;
      return matchedGoal && matchedGoal.id !== prevId ? [matchedGoal] : prev;
    });

    setSelectedResponsible((prev) => {
      const prevId = prev?.[0]?.id ?? null;
      return responsibleUser && responsibleUser.id !== prevId
        ? [responsibleUser]
        : prev;
    });
  }, [
    allProjects,
    allCompanyUsers,
    watch("project_id"),
    watch("goal_id"),
    watch("responsible_user_id"),
  ]);

  useEffect(() => {
    if (selectedGoal.length > 0) {
      const goalIds = selectedGoal.map((p) => p.id);
      setValue("goal_id", goalIds[0], { shouldValidate: true });
    }
  }, [selectedGoal]);

  useEffect(() => {
    if (selectedProject.length > 0) {
      const projectsIds = selectedProject.map((p) => p.id);
      setValue("project_id", projectsIds[0], { shouldValidate: true });
    }
  }, [selectedProject]);

  useEffect(() => {
    if (selectedResponsible.length > 0) {
      const responsibleId = selectedResponsible.map((member) => member.id);
      setValue("responsible_user_id", responsibleId[0], {
        shouldValidate: true,
      });
    }
  }, [selectedResponsible]);

  const [searchParams] = useSearchParams();

  const milestoneId = searchParams.get("milestoneId");

  const [dataFetching, setDataFetching] = useState(false);

  useEffect(() => {
    const existingData = localStorage.getItem("milestoneFormData");
    if (existingData && milestoneId) {
      setDataFetching(true);
      const parsedData = JSON.parse(existingData);
      reset(parsedData);

      setTimeout(() => {
        setDataFetching(false);
      }, 2500);
    }
    localStorage.removeItem("milestoneFormData");
  }, []);

  useEffect(() => {
    if (projectTouched)
      if (selectedProject.length > 0) clearErrors("project");
      else
        setError("project", {
          type: "manual",
          message: "Please select a project",
        });
  }, [selectedProject, projectTouched, setError, clearErrors]);

  useEffect(() => {
    if (goalTouched)
      if (selectedGoal.length > 0) clearErrors("goal");
      else
        setError("goal", {
          type: "manual",
          message: "Please select a goal",
        });
  }, [selectedGoal, goalTouched, setError, clearErrors]);

  return (
    <form className="px-6 pt-4 flex-1 overflow-auto border-b" ref={formRef}>
      <div className="space-y-5">
        {/* Name */}
        <div>
          <p className="text-gray-500 text-xs 2xl:text-sm mb-2">
            Tasks contribute to broader goals. Add a task that contributes to
            your goal: Species Count Change and helps make real progress in your
            program.
          </p>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Task name <span className="text-red-600">*</span>
          </label>
          <input
            {...register("name", {
              required: "Task name is required",
            })}
            className="w-full border text-sm 2xl:text-base border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500"
            placeholder="Add a name"
          />
          {errors.name && (
            <p className="text-red-500 text-xs 2xl:text-sm pt-1">
              {String(errors.name.message)}
            </p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Task description <span className="text-red-600">*</span>
          </label>
          <textarea
            {...register("description", {
              required: "Task description is required",
            })}
            className="w-full text-sm 2xl:text-base border border-gray-300 rounded-md p-3 focus:outline-none focus:border-blue-500"
            placeholder="Type anything..."
          />
          {errors.description && (
            <p className="text-red-500 text-xs 2xl:text-sm pt-1">
              {String(errors.description.message)}
            </p>
          )}
        </div>

        {/* Dates */}
        <div className="flex gap-4">
          <div className="w-1/2">
            <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
              Start date <span className="text-red-600">*</span>
            </label>
            <DateInput
              {...register("start_date", {
                required: "Start Date is required",
                validate: (value) =>
                  !values.end_date ||
                  new Date(value) <= new Date(values.end_date)
                    ? true
                    : "Start Date must be before End Date",
              })}
              minDate={startDate}
              maxDate={endDate}
              value={values.start_date}
            />
            {errors.start_date && (
              <p className="text-red-500 text-xs 2xl:text-sm pt-1">
                {String(errors.start_date.message)}
              </p>
            )}
          </div>

          <div className="w-1/2">
            <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
              End date <span className="text-red-600">*</span>
            </label>
            <DateInput
              {...register("end_date", {
                required: "End Date is required",
                validate: (value) =>
                  !values.start_date ||
                  new Date(value) >= new Date(values.start_date)
                    ? true
                    : "End Date must be after Start Date",
              })}
              minDate={startDate}
              maxDate={endDate}
              value={values.end_date}
            />
            {errors.end_date && (
              <p className="text-red-500 text-xs 2xl:text-sm pt-1">
                {String(errors.end_date.message)}
              </p>
            )}
          </div>
        </div>

        {/* Project */}
        <div onClick={() => setProjectTouched(true)}>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Project<span className="text-red-600"> *</span>
          </label>
          <MultiSelectDropdown
            selected={selectedProject}
            onChange={setSelectedProject}
            options={allProjects}
            labelKey="name"
            valueKey="id"
            placeholder="Search Projects..."
            singleSelect
            triggerDirty={() => setProjectTouched(true)}
            loading={dataFetching || dataLoading}
            alwaysShowOptions={true}
          />
          {errors.project && (
            <p className="text-red-500 text-xs 2xl:text-sm pt-1">
              {String(errors.project.message)}
            </p>
          )}
          <p className="text-gray-500 text-xs my-2">
            Choose the project where this milestone will have impact.
          </p>
        </div>

        <div onClick={() => setGoalTouched(true)}>
          <label className="block text-gray-700 mb-3 text-sm 2xl:text-base">
            Goal<span className="text-red-600"> *</span>
          </label>
          <MultiSelectDropdown
            selected={selectedGoal}
            onChange={setSelectedGoal}
            options={allGoals}
            labelKey="name"
            valueKey="id"
            placeholder="Search Goals..."
            singleSelect
            triggerDirty={() => setGoalTouched(true)}
            loading={dataFetching || dataLoading}
            alwaysShowOptions={true}
          />
          {errors.goal && (
            <p className="text-red-500 text-xs 2xl:text-sm pt-1">
              {String(errors.goal.message)}
            </p>
          )}
          <p className="text-gray-500 text-xs my-2">
            Link the Task with a specific goal to track its progress
          </p>
        </div>

        {/* Responsible */}
        <div className="mb-3">
          <label className="block text-gray-700 mb-2 text-sm 2xl:text-base">
            Responsible
          </label>
          <div className="pb-3">
            <MultiSelectDropdown
              selected={selectedResponsible}
              onChange={setSelectedResponsible}
              fetchOptions={fetchUsers}
              labelKey="full_name"
              valueKey="email"
              placeholder="Search users..."
              singleSelect
              triggerDirty={() => false}
              showValueKey={true}
              queryLengthCheck={1}
              loading={dataFetching || dataLoading}
            />
          </div>
        </div>
        {onDelete && (
          <div className="pb-5">
            <button
              className="text-red-500 border-2 hover:bg-red-100 space-x-2 font-semibold rounded-md border-red-500 flex items-center cursor-pointer px-3 py-2 text-xs 2xl:text-sm"
              type="button"
              onClick={onDelete}
            >
              <img src="/icon_delete.svg" className="w-5 h-5" alt="" />
              <span>Delete Task</span>
            </button>
          </div>
        )}
      </div>
    </form>
  );
};

export default MilestoneForm;
